import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getDramaById, getRelatedDramas, getActorName } from "@/utils/content";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import DramaCard from "@/components/feature/DramaCard";
import Toast from "@/components/feature/Toast";
import DetailHero from "./components/DetailHero";
import EpisodeList from "./components/EpisodeList";

export default function Detail() {
  const { id = "" } = useParams();
  const drama = getDramaById(id);
  const [toast, setToast] = useState("");

  if (!drama) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能已下架或链接有误</p>
        <Link
          to="/category"
          className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回影视分类
        </Link>
      </div>
    );
  }

  const related = getRelatedDramas(drama, 5);
  const paragraphs = drama.fullSynopsis.split("\n\n");

  return (
    <div>
      <title>{`${drama.title} - 善良的嫂子介绍与解析`}</title>
      <meta
        name="description"
        content={`《${drama.title}》剧情介绍、演员名单与精彩解析。${drama.synopsis}`}
      />
      <meta name="keywords" content={`${drama.title},${drama.genre},剧情介绍,演员名单,影视解析`} />

      <DetailHero drama={drama} onFeedback={setToast} />

      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumbs
          items={[
            { label: "影视分类", to: `/category?cat=${drama.category}` },
            { label: drama.category, to: `/category?cat=${drama.category}` },
            { label: drama.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">剧情简介</h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {paragraphs.map((para) => (
                  <p key={para.slice(0, 12)}>{para}</p>
                ))}
              </div>
            </section>

            {drama.episodeList.length > 0 && (
              <section>
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950 md:text-xl">剧集目录</h2>
                <EpisodeList episodes={drama.episodeList} />
                <p className="mt-2 text-xs text-foreground-400">本站仅提供剧情介绍，不提供播放服务。</p>
              </section>
            )}

            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
                <Link to="/category" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                  更多作品 →
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-3">
                {related.slice(0, 3).map((item) => (
                  <DramaCard key={item.id} drama={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">剧集看点</h2>
              <ul className="mt-3 space-y-3">
                {drama.highlights.map((point) => (
                  <li key={point} className="flex gap-2 text-sm leading-relaxed text-foreground-600">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-lightbulb-flash-line"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">演职人员</h2>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">导演</dt>
                  <dd className="text-right text-foreground-800">{drama.director}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">编剧</dt>
                  <dd className="text-right text-foreground-800">{drama.writer}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">类型</dt>
                  <dd className="text-right text-foreground-800">{drama.genre}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-right text-foreground-800">{drama.region}</dd>
                </div>
              </dl>
              <div className="mt-4 border-t border-background-200 pt-4">
                <p className="text-sm text-foreground-500">主要演员</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {drama.cast.map((cid) => (
                    <Link
                      key={cid}
                      to={`/actor/${cid}`}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {getActorName(cid)}
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">所属分类</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/category?cat=${drama.category}`}
                  className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1.5 text-sm text-primary-700 transition-colors hover:bg-primary-200"
                >
                  <span className="flex h-4 w-4 items-center justify-center"><i className="ri-folder-line"></i></span>
                  {drama.category}
                </Link>
                <Link
                  to={`/category`}
                  className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-background-200"
                >
                  {drama.genre}
                </Link>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-primary-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">更多相关推荐</h2>
              <div className="mt-2">
                {related.slice(3).map((item) => (
                  <DramaCard key={item.id} drama={item} variant="compact" />
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </div>
  );
}